import { MultiSeriesComparisonChart, WaveformChart } from '../respiratory-muscle/RespiratoryMuscleCharts';
import AssessmentHistoryComparisonCard, {
  type AssessmentHistoryComparisonEntry,
} from '../components/AssessmentHistoryComparisonCard';
import {
  buildNormalizedPressureComparison,
  buildWavePoints,
  getRespiratoryPressureVariationStatus,
  getRespiratorySeverityTone,
  summarizePimaxPayload,
  getPimaxPositionSeries,
  getPimaxPrimarySeries,
  getPimaxPositionLabel,
  getPimaxPosturalComparison,
  type PimaxResultPayload,
} from '../respiratory-muscle/respiratoryMuscleDomain';
import {
  getAssessmentInterpretationOverride,
  resolveAssessmentInterpretationText,
} from '../utils/interpretationOverride';

export const getPimaxSeverityLabel = (payload?: PimaxResultPayload | null): string => summarizePimaxPayload(payload).severity;

export const getPimaxSeverityTone = (payload?: PimaxResultPayload | null): string =>
  getRespiratorySeverityTone(summarizePimaxPayload(payload).tone);

export const getPimaxInterpretationText = (payload?: PimaxResultPayload | null): string => {
  const summary = summarizePimaxPayload(payload);
  if (summary.max == null) return 'Não há valor válido de PImax para interpretar.';
  const percentSegment = summary.percentPredicted != null
    ? `, correspondente a ${summary.percentPredicted.toFixed(1)}% do predito`
    : '';
  const comparisonSegment = summary.lowerLimit != null && summary.belowLowerLimit != null
    ? summary.belowLowerLimit
      ? `, abaixo do limite inferior esperado de ${summary.lowerLimit.toFixed(1)} cmH2O.`
      : `, dentro da faixa esperada (limite inferior de ${summary.lowerLimit.toFixed(1)} cmH2O).`
    : '. O limite inferior esperado não pôde ser determinado com os dados disponíveis.';
  const automaticInterpretation = `A magnitude da PImax (${summary.positionLabel.toLowerCase()}) foi de ${summary.max.toFixed(1)} cmH2O${percentSegment}${comparisonSegment}${!summary.referenceApplicable ? ' A referência normativa não foi aplicada a esta posição/técnica.' : ''}`;
  const professionalOverride = getAssessmentInterpretationOverride(payload);
  if (professionalOverride) return professionalOverride;
  const resolved = resolveAssessmentInterpretationText(
    payload,
    automaticInterpretation,
  );
  return resolved.includes('Resultado em relação ao valor previsto') ? automaticInterpretation : resolved;
};

interface PimaxResultRendererProps {
  payload: PimaxResultPayload | null | undefined;
  chartImages?: Record<string, string> | null;
  assessmentDate?: string | null;
  comparisonHistory?: AssessmentHistoryComparisonEntry[];
}

export default function PimaxResultRenderer(props: PimaxResultRendererProps) {
  const series = getPimaxPositionSeries(props.payload);
  const primary = getPimaxPrimarySeries(props.payload);
  const comparison = getPimaxPosturalComparison(props.payload);
  return <div className="space-y-5">
    {series.length > 1 && <section className="overflow-x-auto rounded-2xl border border-slate-200 bg-white p-5">
      <h3 className="font-bold text-slate-900">PImax por posição</h3>
      <table className="mt-3 w-full text-left text-sm"><thead><tr><th className="p-2">Posição</th><th className="p-2">Magnitude</th><th className="p-2">Contexto</th></tr></thead><tbody>{series.map((item) => { const summary = summarizePimaxPayload(item.result); return <tr key={item.id} className="border-t"><td className="p-2">{getPimaxPositionLabel(item)}{item.id === primary?.id ? ' · principal' : ''}</td><td className="p-2">{item.notPerformedReason ? 'Não realizada' : summary.max != null ? `${summary.max.toFixed(1)} cmH2O` : 'Sem medida'}</td><td className="p-2">{item.notPerformedReason ?? [item.technique.initialVolume === 'RV' ? 'VR' : item.technique.initialVolume === 'FRC' ? 'CRF' : null, item.technique.interface === 'mouthpiece' ? 'Bocal' : item.technique.interface === 'mask' ? 'Máscara' : null, item.technique.device, item.technique.lipLeak ? 'Fuga labial' : null, item.technique.facialSupport ? 'Suporte da face' : null, item.technique.cooperationLimited ? 'Cooperação limitada' : null, item.technique.orthopnea ? 'Ortopneia' : null, item.technique.notes].filter(Boolean).join(' · ')}</td></tr>; })}</tbody></table>
      {comparison && <p className="mt-4 text-sm text-slate-600">{comparison.reductionPercent != null ? `Variação postural da magnitude (sentado para supino): ${comparison.reductionPercent >= 0 ? 'redução de' : 'aumento de'} ${Math.abs(comparison.reductionPercent).toFixed(1)}%. ` : 'Comparação postural não calculada: técnica incompatível ou dados insuficientes. '}{comparison.interpretation}</p>}
    </section>}
    <SinglePimaxResultRenderer {...props} payload={primary ? { ...primary.result, report: props.payload?.report ?? primary.result.report } : props.payload} chartImages={props.chartImages ?? primary?.result.chartImages}/>
    {series.filter((item) => item.id !== primary?.id && !item.notPerformedReason).map((item) => <details key={item.id} className="rounded-2xl border border-slate-200 bg-white p-5"><summary className="cursor-pointer font-semibold">{getPimaxPositionLabel(item)} — resultados e curvas</summary><div className="mt-5"><SinglePimaxResultRenderer {...props} payload={item.result} chartImages={item.result.chartImages}/></div></details>)}
  </div>;
}

function SinglePimaxResultRenderer({
  payload,
  chartImages = null,
  assessmentDate = null,
  comparisonHistory = [],
}: PimaxResultRendererProps) {
  const summary = summarizePimaxPayload(payload);
  const metrics = payload?.breathelink?.metrics ?? [];
  const plotBreaths = payload?.breathelink?.plotBreaths ?? [];
  const selectedBreaths = payload?.breathelink?.selectedBreaths?.length
    ? payload.breathelink.selectedBreaths
    : payload?.breathelink?.summary?.bestBreathNumbers ?? [];
  const normalizedCurves = plotBreaths.length ? buildNormalizedPressureComparison(plotBreaths, selectedBreaths.length ? selectedBreaths : plotBreaths.map((breath) => breath.breathNumber)) : null;
  const firstWaveform = plotBreaths[0] ?? null;
  const hasSustainedMetrics = metrics.some((metric) => metric.sustainedPressure1s != null);
  const variationStatus = getRespiratoryPressureVariationStatus(
    summary.quality?.variationTop2Percent,
    summary.quality?.maneuversAnalyzed ?? summary.measurements.length,
  );

  return (
    <div className="space-y-6">
      <div className="grid gap-6 md:grid-cols-5">
        <div className="rounded-[28px] border border-slate-200 bg-white p-8 text-center shadow-sm md:col-span-2">
          <p className="mb-4 text-xs font-black uppercase tracking-[0.25em] text-slate-600">PRESSÃO INSPIRATÓRIA MÁXIMA</p>
          <div className="text-6xl font-black leading-none text-slate-900">{summary.max != null ? summary.max.toFixed(0) : '-'}</div>
          <div className="mt-2 text-sm font-semibold text-slate-700">{summary.max != null ? 'cmH2O' : 'sem valor válido'}</div>
          <div className="mt-2 text-sm text-slate-600">{summary.positionLabel}</div>
          <div className={`mt-4 inline-flex rounded-full px-4 py-1.5 text-xs font-bold shadow-sm ${getPimaxSeverityTone(payload)}`}>
            {summary.severity}
          </div>
        </div>

        <div className="flex flex-col justify-center space-y-4 md:col-span-3">
          <div>
            <h4 className="text-2xl font-black tracking-tight text-slate-800">Interpretação</h4>
            <p className="mt-3 max-w-3xl text-sm leading-relaxed text-slate-600">{getPimaxInterpretationText(payload)}</p>
            <ul className="mt-3 space-y-1 text-xs leading-relaxed text-slate-500">
              {summary.interpretation.lines.filter((line) => !line.startsWith('PImax ')).map((line, index) => <li key={`pimax-summary-line-${index}`}>{line}</li>)}
            </ul>
          </div>
          {(summary.predicted != null || summary.quality?.variationTop2Percent != null) && <div className="grid gap-3 text-sm text-slate-600 md:grid-cols-2">
            {summary.predicted != null && <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
              Predito: <span className="font-semibold text-slate-900">{summary.predicted != null ? `${summary.predicted.toFixed(1)} cmH2O` : '-'}</span>
            </div>}
            {summary.quality?.variationTop2Percent != null && <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
              Variação entre as 2 melhores curvas:{' '}
              <span className="font-semibold text-slate-900">
                {summary.quality.variationTop2Percent.toFixed(2)}%{variationStatus ? ` (${variationStatus})` : ''}
              </span>
            </div>}
          </div>}
        </div>
      </div>

      <AssessmentHistoryComparisonCard
        testKey="PIMAX"
        currentPayload={payload}
        currentAssessmentDate={assessmentDate}
        history={comparisonHistory}
      />

      <div className="grid gap-6 md:grid-cols-3">
        {summary.sustained1s != null && <Metric label="Sustentada por 1 segundo" value={`${summary.sustained1s.toFixed(1)} cmH2O`} />}
        {summary.percentPredicted != null && <Metric label="% do predito" value={`${summary.percentPredicted.toFixed(1)}%`} />}
        {summary.lowerLimit != null && <Metric label="Limite inferior esperado" value={`${summary.lowerLimit.toFixed(1)} cmH2O`} />}
        <Metric label="Curvas/medidas" value={String(summary.quality?.maneuversAnalyzed ?? summary.measurements.length)} />
      </div>

      {chartImages?.comparison ? (
        <div className="rounded-[24px] border border-slate-100 bg-white p-6 shadow-sm">
          <div className="text-[10px] font-black uppercase tracking-[0.24em] text-slate-400">Curvas de pressão normalizadas</div>
          <img src={chartImages.comparison} alt="Curvas de PImax" className="mt-4 w-full rounded-2xl border border-slate-100" />
        </div>
      ) : normalizedCurves ? (
        <MultiSeriesComparisonChart title="Curvas de pressão normalizadas" unit="cmH2O" data={normalizedCurves.data} seriesMeta={normalizedCurves.seriesMeta} />
      ) : null}

      {chartImages?.individual ? (
        <div className="rounded-[24px] border border-slate-100 bg-white p-6 shadow-sm">
          <div className="text-[10px] font-black uppercase tracking-[0.24em] text-slate-400">Curva individual</div>
          <img src={chartImages.individual} alt="Curva individual de PImax" className="mt-4 w-full rounded-2xl border border-slate-100" />
        </div>
      ) : firstWaveform ? (
        <WaveformChart title={`Curva ${firstWaveform.breathNumber}`} color="#2563eb" unit="cmH2O" dataKey="pressure" data={buildWavePoints(firstWaveform)} />
      ) : null}

      {metrics.length ? (
        <div className="overflow-x-auto rounded-[24px] border border-slate-100 bg-white shadow-sm">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left">Curva</th>
                <th className="px-4 py-3 text-left">Tins</th>
                <th className="px-4 py-3 text-left">Pico</th>
                {hasSustainedMetrics && <th className="px-4 py-3 text-left">Sustentada 1 s</th>}
              </tr>
            </thead>
            <tbody>
              {metrics.map((metric) => (
                <tr key={metric.breathNumber} className="border-t">
                  <td className="px-4 py-3">{metric.breathNumber}</td>
                  <td className="px-4 py-3">{metric.tins.toFixed(2)} s</td>
                  <td className="px-4 py-3">{metric.peakPressure.toFixed(2)} cmH2O</td>
                  {hasSustainedMetrics && <td className="px-4 py-3">{metric.sustainedPressure1s != null ? `${metric.sustainedPressure1s.toFixed(2)} cmH2O` : 'Não informada'}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : summary.measurements.length ? (
        <div className="rounded-[24px] border border-slate-100 bg-slate-50 p-5 shadow-sm">
          <div className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Medições manuais</div>
          <div className="mt-3 text-sm text-slate-700">{summary.measurements.map((value) => `${value.toFixed(1)} cmH2O`).join(' - ')}</div>
        </div>
      ) : null}
    </div>
  );
}

const Metric = ({ label, value }: { label: string; value: string }) => (
  <div className="rounded-[24px] border border-slate-100 bg-white p-5 shadow-sm">
    <div className="text-[10px] font-black uppercase tracking-[0.22em] text-slate-400">{label}</div>
    <div className="mt-2 text-3xl font-black text-slate-900">{value}</div>
  </div>
);
